import { useState, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './hooks/useAuthStore';
import { Layout } from './components/Layout';
import { AuthLayout } from './components/AuthLayout';
import { ToastProvider } from './components/Toast';
import { ConsentModal, useNdpaConsent } from './components/ConsentModal';
import { useInitAuth } from './hooks/useInitAuth';
import { TherapistWorkspaceLayout } from './components/workspace/TherapistWorkspaceLayout';
import { ClientWorkspaceLayout } from './components/workspace/ClientWorkspaceLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SkeletonText } from './components/Skeleton';

const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() =>
  import('./pages/RegisterPage').then((m) => ({ default: m.RegisterPage }))
);
const AssessmentPage = lazy(() =>
  import('./pages/AssessmentPage').then((m) => ({ default: m.AssessmentPage }))
);
const TherapistListPage = lazy(() =>
  import('./pages/TherapistListPage').then((m) => ({ default: m.TherapistListPage }))
);
const TherapistProfilePage = lazy(() =>
  import('./pages/TherapistProfilePage').then((m) => ({ default: m.TherapistProfilePage }))
);
const TherapistOnboardingPage = lazy(() =>
  import('./pages/TherapistOnboardingPage').then((m) => ({ default: m.TherapistOnboardingPage }))
);
const TherapistEarningsPage = lazy(() =>
  import('./pages/TherapistEarningsPage').then((m) => ({ default: m.TherapistEarningsPage }))
);
const BookingPage = lazy(() =>
  import('./pages/BookingPage').then((m) => ({ default: m.BookingPage }))
);
const ClientDashboard = lazy(() =>
  import('./pages/ClientDashboard').then((m) => ({ default: m.ClientDashboard }))
);
const TherapistDashboard = lazy(() =>
  import('./pages/TherapistDashboard').then((m) => ({ default: m.TherapistDashboard }))
);
const SessionPage = lazy(() =>
  import('./pages/SessionPage').then((m) => ({ default: m.SessionPage }))
);
const ProgressPage = lazy(() =>
  import('./pages/ProgressPage').then((m) => ({ default: m.ProgressPage }))
);
const ChatPage = lazy(() => import('./pages/ChatPage').then((m) => ({ default: m.ChatPage })));
const SOAPNotePage = lazy(() =>
  import('./pages/SOAPNotePage').then((m) => ({ default: m.SOAPNotePage }))
);
const GroupSessionsPage = lazy(() =>
  import('./pages/GroupSessionsPage').then((m) => ({ default: m.GroupSessionsPage }))
);
const ReferralPage = lazy(() =>
  import('./pages/ReferralPage').then((m) => ({ default: m.ReferralPage }))
);
const BadgesPage = lazy(() =>
  import('./pages/BadgesPage').then((m) => ({ default: m.BadgesPage }))
);
const ClientToolsPage = lazy(() =>
  import('./pages/ClientToolsPage').then((m) => ({ default: m.ClientToolsPage }))
);
const GroundingPage = lazy(() =>
  import('./pages/GroundingPage').then((m) => ({ default: m.GroundingPage }))
);
const WorryDumpPage = lazy(() =>
  import('./pages/WorryDumpPage').then((m) => ({ default: m.WorryDumpPage }))
);
const SessionPrepPage = lazy(() =>
  import('./pages/SessionPrepPage').then((m) => ({ default: m.SessionPrepPage }))
);
const CrisisSupportPage = lazy(() =>
  import('./pages/CrisisSupportPage').then((m) => ({ default: m.CrisisSupportPage }))
);
const AppGuidePage = lazy(() =>
  import('./pages/AppGuidePage').then((m) => ({ default: m.AppGuidePage }))
);
const JournalPage = lazy(() =>
  import('./pages/JournalPage').then((m) => ({ default: m.JournalPage }))
);
const ActivitiesPage = lazy(() =>
  import('./pages/ActivitiesPage').then((m) => ({ default: m.ActivitiesPage }))
);
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const ClientSessionsPage = lazy(() =>
  import('./pages/ClientSessionsPage').then((m) => ({ default: m.ClientSessionsPage }))
);
const ClientMessagesPage = lazy(() =>
  import('./pages/ClientMessagesPage').then((m) => ({ default: m.ClientMessagesPage }))
);
const CorporatePortalPage = lazy(() =>
  import('./pages/CorporatePortalPage').then((m) => ({ default: m.CorporatePortalPage }))
);
const CampusDashboardPage = lazy(() =>
  import('./pages/CampusDashboardPage').then((m) => ({ default: m.CampusDashboardPage }))
);
const TherapistAvailabilityPage = lazy(() =>
  import('./pages/TherapistAvailabilityPage').then((m) => ({
    default: m.TherapistAvailabilityPage,
  }))
);
const TherapistEditProfilePage = lazy(() =>
  import('./pages/TherapistEditProfilePage').then((m) => ({
    default: m.TherapistEditProfilePage,
  }))
);
const TherapistSettingsPage = lazy(() =>
  import('./pages/TherapistSettingsPage').then((m) => ({
    default: m.TherapistSettingsPage,
  }))
);
const PsychoeducationPage = lazy(() =>
  import('./pages/PsychoeducationPage').then((m) => ({ default: m.PsychoeducationPage }))
);
const ArticlePage = lazy(() =>
  import('./pages/ArticlePage').then((m) => ({ default: m.ArticlePage }))
);
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
);
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const TermsPage = lazy(() => import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage }))
);
const HmoPartnersPage = lazy(() =>
  import('./pages/HmoPartnersPage').then((m) => ({ default: m.HmoPartnersPage }))
);
const TherapyGuidePage = lazy(() =>
  import('./pages/TherapyGuidePage').then((m) => ({ default: m.TherapyGuidePage }))
);
const CorporateTrainingsPage = lazy(() =>
  import('./pages/CorporateTrainingsPage').then((m) => ({ default: m.CorporateTrainingsPage }))
);
const HelpCentrePage = lazy(() =>
  import('./pages/HelpCentrePage').then((m) => ({ default: m.HelpCentrePage }))
);
const TherapistDirectoryPage = lazy(() =>
  import('./pages/TherapistDirectoryPage').then((m) => ({ default: m.TherapistDirectoryPage }))
);
const ScreeningPage = lazy(() =>
  import('./pages/ScreeningPage').then((m) => ({ default: m.ScreeningPage }))
);

function PageFallback() {
  return (
    <div className="page p-8">
      <SkeletonText lines={4} />
    </div>
  );
}

function PrivateRoute({ element, role }: { element: React.ReactElement; role?: string }) {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return element;
}

function ConsentGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  const hasConsent = useNdpaConsent();
  const [accepted, setAccepted] = useState(false);
  if (user && !hasConsent && !accepted) {
    return <ConsentModal onAccept={() => setAccepted(true)} />;
  }
  return <>{children}</>;
}

function AuthBootstrap({ children }: { children: React.ReactNode }) {
  useInitAuth();
  return <>{children}</>;
}

export default function App() {
  return (
    <ToastProvider>
      <AuthBootstrap>
        <ConsentGate>
          <ErrorBoundary>
            <div className="min-h-screen">
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route element={<AuthLayout />}>
                    <Route path="login" element={<LoginPage />} />
                    <Route path="register" element={<RegisterPage />} />
                    <Route path="register/therapist" element={<RegisterPage role="therapist" />} />
                    <Route
                      path="register/corporate"
                      element={<RegisterPage role="corporate_hr" />}
                    />
                    <Route path="register/student" element={<RegisterPage role="student" />} />
                    <Route path="forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="reset-password" element={<ResetPasswordPage />} />
                  </Route>
                  <Route path="/" element={<Layout />}>
                    {/* Public */}
                    <Route index element={<HomePage />} />
                    <Route path="assessment" element={<AssessmentPage />} />
                    <Route path="assessment/:screen" element={<ScreeningPage />} />
                    <Route path="find/:slug" element={<TherapistDirectoryPage />} />
                    <Route path="help" element={<HelpCentrePage />} />
                    <Route path="therapists" element={<TherapistListPage />} />
                    <Route path="therapists/:id" element={<TherapistProfilePage />} />
                    <Route path="groups" element={<GroupSessionsPage />} />
                    <Route path="learn" element={<PsychoeducationPage />} />
                    <Route path="learn/therapy-guide" element={<TherapyGuidePage />} />
                    <Route path="learn/:slug" element={<ArticlePage />} />
                    <Route path="privacy" element={<PrivacyPage />} />
                    <Route path="terms" element={<TermsPage />} />
                    <Route path="about" element={<AboutPage />} />
                    <Route path="contact" element={<ContactPage />} />
                    <Route path="hmo-partners" element={<HmoPartnersPage />} />
                    <Route path="corporate/trainings" element={<CorporateTrainingsPage />} />

                    {/* Client workspace */}
                    <Route element={<ClientWorkspaceLayout />}>
                      <Route path="dashboard" element={<ClientDashboard />} />
                      <Route path="care/find" element={<TherapistListPage />} />
                      <Route path="care/find/:id" element={<TherapistProfilePage />} />
                      <Route path="care/resources" element={<PsychoeducationPage />} />
                      <Route path="care/resources/therapy-guide" element={<TherapyGuidePage />} />
                      <Route path="care/resources/:slug" element={<ArticlePage />} />
                      <Route path="sessions" element={<ClientSessionsPage />} />
                      <Route path="messages" element={<ClientMessagesPage />} />
                      <Route path="progress" element={<ProgressPage />} />
                      <Route path="referrals" element={<ReferralPage />} />
                      <Route path="tools" element={<ClientToolsPage />} />
                      <Route path="tools/grounding" element={<GroundingPage />} />
                      <Route path="tools/worry-dump" element={<WorryDumpPage />} />
                      <Route path="tools/session-prep" element={<SessionPrepPage />} />
                      <Route path="tools/crisis" element={<CrisisSupportPage />} />
                      <Route path="tools/guide" element={<AppGuidePage />} />
                      <Route path="journal" element={<JournalPage />} />
                      <Route path="activities/:slug?" element={<ActivitiesPage />} />
                      <Route path="badges" element={<BadgesPage />} />
                      <Route path="profile" element={<ProfilePage />} />
                    </Route>

                    <Route
                      path="book/:therapistId"
                      element={<PrivateRoute element={<BookingPage />} />}
                    />
                    <Route
                      path="session/:bookingId"
                      element={<PrivateRoute element={<SessionPage />} />}
                    />
                    <Route
                      path="chat/:bookingId"
                      element={<PrivateRoute element={<ChatPage />} />}
                    />
                    {/* Therapist workspace */}
                    <Route element={<TherapistWorkspaceLayout />}>
                      <Route path="therapist/dashboard" element={<TherapistDashboard />} />
                      <Route
                        path="therapist/availability"
                        element={<TherapistAvailabilityPage />}
                      />
                      <Route path="therapist/earnings" element={<TherapistEarningsPage />} />
                      <Route path="therapist/profile" element={<TherapistEditProfilePage />} />
                      <Route path="therapist/settings" element={<TherapistSettingsPage />} />
                    </Route>

                    <Route
                      path="therapist/onboarding"
                      element={
                        <PrivateRoute element={<TherapistOnboardingPage />} role="therapist" />
                      }
                    />

                    <Route
                      path="therapist/notes/:bookingId"
                      element={<PrivateRoute element={<SOAPNotePage />} role="therapist" />}
                    />

                    {/* Corporate HR */}
                    <Route
                      path="corporate/dashboard"
                      element={
                        <PrivateRoute element={<CorporatePortalPage />} role="corporate_hr" />
                      }
                    />
                    <Route
                      path="campus/dashboard"
                      element={<PrivateRoute element={<CampusDashboardPage />} />}
                    />
                  </Route>
                </Routes>
              </Suspense>
            </div>
          </ErrorBoundary>
        </ConsentGate>
      </AuthBootstrap>
    </ToastProvider>
  );
}
